interface CacheItem<T> {
  data: T
  expireTime: number
}

export interface CacheConfig {
  enable?: boolean
  /** 缓存隔离范围：public 跨登录态复用，auth 按当前 token 主体隔离 */
  scope?: 'public' | 'auth'
  /** 缓存时间（毫秒），默认 5000ms */
  ttl?: number
}

class CacheManager {
  private cache = new Map<string, CacheItem<unknown>>()
  private pendingRequests = new Map<string, Promise<unknown>>()

  set(key: string, data: unknown, ttl = 5000): void {
    this.cache.set(key, { data, expireTime: Date.now() + ttl })
  }

  get(key: string): unknown {
    const item = this.cache.get(key)
    if (!item) return null
    if (Date.now() > item.expireTime) {
      this.cache.delete(key)
      return null
    }
    return item.data
  }

  has(key: string): boolean {
    const item = this.cache.get(key)
    if (!item) return false
    if (Date.now() > item.expireTime) {
      this.cache.delete(key)
      return false
    }
    return true
  }

  delete(key: string): void {
    this.cache.delete(key)
    this.pendingRequests.delete(key)
  }

  clear(): void {
    this.cache.clear()
    this.pendingRequests.clear()
  }

  clearExpired(): void {
    const now = Date.now()
    this.cache.forEach((item, key) => {
      if (now > item.expireTime) {
        this.cache.delete(key)
      }
    })
  }

  size(): number {
    return this.cache.size
  }

  getPending(key: string): Promise<unknown> | null {
    return this.pendingRequests.get(key) || null
  }

  setPending(key: string, request: Promise<unknown>): Promise<unknown> {
    this.pendingRequests.set(key, request)
    request
      .finally(() => {
        if (this.pendingRequests.get(key) === request) {
          this.pendingRequests.delete(key)
        }
      })
      .catch(() => undefined)
    return request
  }
}

export const cacheManager = new CacheManager()

let cleanupTimer: ReturnType<typeof setInterval> | null = null

/** 启动缓存清理定时器（仅初始化一次） */
export const ensureCacheCleanupStarted = (): void => {
  if (cleanupTimer !== null) return
  cleanupTimer = setInterval(() => {
    cacheManager.clearExpired()
  }, 60000)
}

/** 停止缓存清理定时器 */
export const stopCacheCleanup = (): void => {
  if (cleanupTimer !== null) {
    clearInterval(cleanupTimer)
    cleanupTimer = null
  }
}
